<template>
  <div>
    <RedesignSearchBarCollapsed
      v-if="(collapsable && isCollapsed) || $screen.isUnknown"
      v-model="model"
      :class="
        $screen.isUnknown && {
          'block md:hidden': isPrecollapsed,
          hidden: !isPrecollapsed,
        }
      "
      @click="toggle()"
    />

    <div
      v-if="!(collapsable && isCollapsed) || $screen.isUnknown"
      class="flex flex-col md:flex-row gap-4"
      :class="
        $screen.isUnknown && {
          'hidden md:flex': isPrecollapsed,
          flex: !isPrecollapsed,
        }
      "
    >
      <RedesignSearchDestinationSelect
        v-model="destination"
        class="w-full md:max-w-72"
      />

      <RedesignSearchDateRangePicker
        ref="datePickerRef"
        v-model="period"
        class="w-full md:w-auto"
      />

      <RedesignSearchGuestsSelect
        v-model="guests"
        class="w-full md:w-auto"
        @change="trackGuests"
      />

      <WebccButton variant="cta" class="h-12 grow" @click="performSubmit">
        {{ $t(`${trPrefix}search`) }}
      </WebccButton>

      <WebccButton
        v-if="collapsable"
        variant="white"
        outline
        class="h-12 md:hidden"
        :loading
        @click="toggle"
      >
        {{ $t('www.pages.favorites.close') }}
      </WebccButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DeepReadonly } from 'vue'
import type { SuggestSuggestionV22 } from '~/types/openapi/search-api'

const trPrefix = 'www.components.elements.SearchBox.'

const model = defineModel<SearchBarState>({ required: true })

const props = defineProps<{
  collapsable?: boolean
  loading?: boolean
}>()

const emit = defineEmits<{
  submit: [state: DeepReadonly<SearchBarState>]
}>()

const tracking = useTracking()

const datePickerRef = useTemplateRef('datePickerRef')

const guests = propToRef(model, 'guests')
const period = propToRef(model, 'period')
const destination = customRef<SuggestSuggestionV22 | undefined>(() => ({
  get() {
    return model.value.destination
  },
  set(newValue) {
    model.value = { ...model.value, destination: newValue }

    if (!newValue) return

    tracking.handlers?.qs.destinationSelect(newValue)
    /**
     * DatePicker should only be automatically opened after change within the form.
     * Change from outside should not trigger this opening.
     */
    setTimeout(() => datePickerRef.value?.toggle(true), 0)
  },
}))

const screen = useScreen()
const isScreenSmall = screen.breakpoints.smaller('md')
const hasMinData = computed(
  () => !!destination.value || isValidDatePeriod(period.value),
)

const [isCollapsed, toggle] = useToggle(props.collapsable && hasMinData.value)

watchImmediate(isScreenSmall, () => adjustCollapse())

async function performSubmit() {
  tracking.handlers?.qs.search(destination.value, period.value, guests.value)
  emit('submit', readonly(unref(model)))

  adjustCollapse()
}

function trackGuests({ pax, pets }: GuestsValue) {
  tracking.handlers?.qs.paxSelect(pax)
  tracking.handlers?.qs.petsSelect(pets)
}

function adjustCollapse() {
  toggle(isScreenSmall.value && hasMinData.value)
}

const isPrecollapsed = computed(() => props.collapsable && hasMinData.value)
</script>
